import { Table, Tag, Typography, Space, Button } from 'antd';
import { EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getJobStatusTagColor } from '../../utils/jobStatus';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface EventRecord {
  job_id: string;
  trace_id: string;
  span_id?: string;
  namespace: string;
  time: string;
  status?: string;
  level?: string;
  details?: string;
  message?: string;
  metric_name?: string;
  value?: number;
  [key: string]: unknown;
}

interface EventListProps {
  events: EventRecord[];
  loading: boolean;
  table: 'job_status' | 'stat' | 'log';
  page: number;
  pageSize: number;
  hasMore: boolean;
  onPageChange: (page: number, pageSize: number) => void;
}

const LOG_LEVEL_COLORS: Record<string, string> = {
  DEBUG: 'default',
  INFO: 'blue',
  WARN: 'orange',
  ERROR: 'red',
  FATAL: 'magenta',
};

export function EventList({
  events,
  loading,
  table,
  page,
  pageSize,
  hasMore,
  onPageChange,
}: EventListProps) {
  const navigate = useNavigate();

  const baseColumns: ColumnsType<EventRecord> = [
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      width: 180,
      render: (time: string) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(time).fromNow()}
          </Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.time).unix() - dayjs(b.time).unix(),
    },
    {
      title: 'Job ID',
      dataIndex: 'job_id',
      key: 'job_id',
      width: 280,
      ellipsis: true,
      render: (jobId: string) => (
        <Button
          type="link"
          size="small"
          onClick={() => navigate(`/jobs/${jobId}`)}
          icon={<LinkOutlined />}
          style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={jobId}
        >
          {jobId.length > 30 ? `${jobId.substring(0, 30)}...` : jobId}
        </Button>
      ),
    },
    {
      title: 'Namespace',
      dataIndex: 'namespace',
      key: 'namespace',
      width: 200,
      ellipsis: true,
    },
  ];

  const jobStatusColumns: ColumnsType<EventRecord> = [
    ...baseColumns,
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => (
        <Tag color={getJobStatusTagColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (details: string) => details || '-',
    },
  ];

  const logColumns: ColumnsType<EventRecord> = [
    ...baseColumns,
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: string) => (
        <Tag color={LOG_LEVEL_COLORS[level] || 'default'}>{level}</Tag>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
  ];

  const statColumns: ColumnsType<EventRecord> = [
    ...baseColumns,
    {
      title: 'Metric',
      dataIndex: 'metric_name',
      key: 'metric_name',
      width: 150,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value: number) => value?.toLocaleString() ?? '-',
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
    },
  ];

  const columnMap = {
    job_status: jobStatusColumns,
    log: logColumns,
    stat: statColumns,
  };

  const columns = columnMap[table];

  return (
    <Table
      dataSource={events}
      columns={columns}
      loading={loading}
      rowKey={(record) => `${record.job_id}-${record.time}-${record.span_id || ''}`}
      pagination={{
        current: page,
        pageSize,
        // Provide a computed total: if hasMore, add another page worth of items
        total: hasMore ? (page * pageSize) + 1 : (page - 1) * pageSize + events.length,
        onChange: onPageChange,
        showSizeChanger: true,
        pageSizeOptions: ['25', '50', '100'],
        showTotal: (_, range) =>
          `${range[0]}-${range[1]} ${hasMore ? '(more available)' : ''}`,
      }}
      size="middle"
      scroll={{ x: 800 }}
      expandable={{
        expandedRowRender: (record) => (
          <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
            {JSON.stringify(record, null, 2)}
          </pre>
        ),
        expandIcon: ({ onExpand, record }) => (
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => onExpand(record, e)}
          />
        ),
      }}
    />
  );
}
